import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeartPulse, 
  faUserShield, 
  faHandHoldingHeart,
  faBookMedical,
  faUsers,
  faBriefcaseMedical,
  faStethoscope,
  faComments,
  faStar
} from '@fortawesome/free-solid-svg-icons';

const Themes = ({themes}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Palette de couleurs variées pour les thèmes
  const colorPalette = [
    { 
      gradient: 'from-purple-500 to-purple-600',
      bg: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      text: 'text-purple-600',
      border: 'border-purple-200',
      shadow: 'hover:shadow-purple-200/50',
      icon: faHeartPulse
    },
    { 
      gradient: 'from-pink-500 to-pink-600',
      bg: 'bg-pink-50',
      iconBg: 'bg-pink-100',
      text: 'text-pink-600',
      border: 'border-pink-200',
      shadow: 'hover:shadow-pink-200/50',
      icon: faUserShield
    },
    { 
      gradient: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      text: 'text-blue-600',
      border: 'border-blue-200',
      shadow: 'hover:shadow-blue-200/50',
      icon: faHandHoldingHeart
    },
    { 
      gradient: 'from-green-500 to-green-600',
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      text: 'text-green-600',
      border: 'border-green-200',
      shadow: 'hover:shadow-green-200/50',
      icon: faBookMedical
    },
    { 
      gradient: 'from-red-500 to-red-600',
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      text: 'text-red-600',
      border: 'border-red-200',
      shadow: 'hover:shadow-red-200/50',
      icon: faUsers
    },
    { 
      gradient: 'from-indigo-500 to-indigo-600',
      bg: 'bg-indigo-50',
      iconBg: 'bg-indigo-100',
      text: 'text-indigo-600',
      border: 'border-indigo-200',
      shadow: 'hover:shadow-indigo-200/50',
      icon: faBriefcaseMedical
    },
    { 
      gradient: 'from-teal-500 to-teal-600',
      bg: 'bg-teal-50',
      iconBg: 'bg-teal-100',
      text: 'text-teal-600',
      border: 'border-teal-200',
      shadow: 'hover:shadow-teal-200/50',
      icon: faStethoscope
    },
    { 
      gradient: 'from-orange-500 to-orange-600',
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      text: 'text-orange-600',
      border: 'border-orange-200',
      shadow: 'hover:shadow-orange-200/50',
      icon: faComments
    },
    { 
      gradient: 'from-yellow-500 to-yellow-600',
      bg: 'bg-yellow-50',
      iconBg: 'bg-yellow-100',
      text: 'text-yellow-600',
      border: 'border-yellow-200',
      shadow: 'hover:shadow-yellow-200/50',
      icon: faStar
    }
  ];

  if (!themes || themes.length === 0) {
    return null;
  }

  return (
    <div className="wow fadeInUp max-w-7xl mx-auto px-4 py-8" data-wow-delay="0.1s">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {themes.map((theme, index) => {
          const colors = colorPalette[index % colorPalette.length];
          const isHovered = hoveredIndex === index;

          return (
            <div
              key={theme.id}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`
                group relative bg-white rounded-2xl overflow-hidden
                border-2 ${colors.border} shadow-lg ${colors.shadow}
                hover:shadow-2xl hover:-translate-y-2
                transition-all duration-500 cursor-pointer
                animate-fade-in-up
              `}
              style={{
                animationDelay: `${index * 0.1}s`
              }}
            >
              {/* Gradient top bar */}
              <div className={`h-1.5 w-full bg-gradient-to-r ${colors.gradient}`}></div>

              {/* Background pattern */}
              <div className={`absolute inset-0 ${colors.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

              {/* Decorative blob */}
              <div className={`
                absolute -top-10 -right-10 w-32 h-32 
                bg-gradient-to-br ${colors.gradient} 
                rounded-full opacity-0 group-hover:opacity-10 
                blur-2xl transition-opacity duration-500
              `}></div>

              {/* Content */}
              <div className="relative p-6">
                {/* Icon */}
                <div className="flex justify-between items-start mb-4">
                  <div className={`
                    w-14 h-14 ${colors.iconBg} rounded-xl
                    flex items-center justify-center
                    group-hover:scale-110 group-hover:rotate-6
                    transition-all duration-500 shadow-md
                  `}>
                    <FontAwesomeIcon 
                      icon={colors.icon}
                      className={`text-2xl ${colors.text}`}
                    />
                  </div>

                  {/* Number badge */}
                  <div className={`
                    w-8 h-8 rounded-full bg-gradient-to-br ${colors.gradient}
                    flex items-center justify-center text-white text-xs font-bold
                    shadow-md
                  `}>
                    {index + 1}
                  </div>
                </div>

                {/* Title */}
                <h4 className={`
                  text-lg font-bold text-gray-800 mb-3 leading-tight
                  group-hover:${colors.text.replace('text-', 'text-')}
                  transition-colors duration-300 min-h-[3.5rem]
                `}>
                  {theme.title}
                </h4>

                {/* Description (if available) */}
                {theme.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                    {theme.description}
                  </p>
                )}

                {/* Bottom action */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className={`text-sm font-semibold ${colors.text} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
                    En savoir plus
                  </span>
                  <div className={`
                    w-8 h-8 rounded-full bg-gradient-to-br ${colors.gradient}
                    flex items-center justify-center
                    transform translate-x-0 group-hover:translate-x-1
                    transition-transform duration-300
                  `}>
                    <svg 
                      className="w-4 h-4 text-white" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Hover shine effect */}
                <div className={`
                  absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent
                  opacity-0 group-hover:opacity-20
                  transform -translate-x-full group-hover:translate-x-full
                  transition-all duration-1000
                  pointer-events-none
                `}></div>
              </div>

              {/* Corner decorations */}
              <div className={`
                absolute top-0 right-0 w-16 h-16 
                border-t-2 border-r-2 ${colors.border}
                opacity-0 group-hover:opacity-100
                transition-opacity duration-500
              `}></div>
              <div className={`
                absolute bottom-0 left-0 w-16 h-16 
                border-b-2 border-l-2 ${colors.border}
                opacity-0 group-hover:opacity-100
                transition-opacity duration-500
              `}></div>
            </div>
          );
        })}
      </div>

      {/* Empty state message if needed */}
      {themes.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-block p-8 bg-gray-50 rounded-2xl">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
            </svg>
            <p className="text-gray-600 font-medium">Aucun thème disponible pour le moment</p>
          </div>
        </div>
      )}

      <style jsx>{`
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Themes;